import { createSlice } from '@reduxjs/toolkit';
import { validateAdviserThunk } from './thunks/validate-adviser-thunk';
import { sendToAdviserThunk } from './thunks/send-to-adviser-thunk';

export interface AdviserState {
  validation: {
    isValidating: boolean;
    isValid: boolean | null;
    adviserId: string | null;
    error: string | null;
  };
  sending: {
    isSending: boolean;
    shareId: string | null;
    timestamp: string | null;
    error: string | null;
  };
}

const initialState: AdviserState = {
  validation: {
    isValidating: false,
    isValid: null,
    adviserId: null,
    error: null
  },
  sending: {
    isSending: false,
    shareId: null,
    timestamp: null,
    error: null
  }
};

const adviserSlice = createSlice({
  name: 'adviser',
  initialState,
  reducers: {
    // Reset validation state
    resetValidation: (state) => {
      state.validation = {
        isValidating: false,
        isValid: null,
        adviserId: null,
        error: null
      };
    },
    // Reset sending state
    resetSending: (state) => {
      state.sending = {
        isSending: false,
        shareId: null,
        timestamp: null,
        error: null
      };
    },
    // Reset all adviser state
    resetAdviser: (state) => {
      state.validation = initialState.validation;
      state.sending = initialState.sending;
    }
  },
  extraReducers: (builder) => {
    // Handle validateAdviserThunk
    builder
      .addCase(validateAdviserThunk.pending, (state) => {
        state.validation.isValidating = true;
        state.validation.isValid = null;
        state.validation.adviserId = null;
        state.validation.error = null;
      })
      .addCase(validateAdviserThunk.fulfilled, (state, action) => {
        state.validation.isValidating = false;
        state.validation.isValid = action.payload.isValid;
        state.validation.adviserId = action.payload.adviserId || null;
        state.validation.error = null;
      })
      .addCase(validateAdviserThunk.rejected, (state, action) => {
        state.validation.isValidating = false;
        state.validation.isValid = false;
        state.validation.adviserId = null;
        state.validation.error = action.error.message || 'Failed to validate adviser email';
      });

    // Handle sendToAdviserThunk
    builder
      .addCase(sendToAdviserThunk.pending, (state) => {
        state.sending.isSending = true;
        state.sending.shareId = null;
        state.sending.timestamp = null;
        state.sending.error = null;
      })
      .addCase(sendToAdviserThunk.fulfilled, (state, action) => {
        state.sending.isSending = false;
        state.sending.shareId = action.payload.shareId ?? null;
        state.sending.timestamp = action.payload.timestamp ?? null;
        state.sending.error = null;
      })
      .addCase(sendToAdviserThunk.rejected, (state, action) => {
        state.sending.isSending = false;
        state.sending.shareId = null;
        state.sending.timestamp = null;
        state.sending.error = action.error.message || 'Failed to send data to adviser';
      });
  }
});

export const { resetValidation, resetSending, resetAdviser } = adviserSlice.actions;
export default adviserSlice.reducer;
